import { OrderStatus } from './enums';

/**
 * Order state machine — declared as DATA so the automation engine, guards, and the
 * UI all read from one table. The automation engine OWNS the side-effects; this
 * module only declares WHICH side-effects a transition implies and WHO may trigger it.
 *
 * Phase 1 happy path:
 *   NEW → PROFORMA_SENT → PAID → INVOICED → SHIPPED → DELIVERED
 * Plus:
 *   SHIPPED → DELIVERY_PROBLEM (tracking poll)
 *   ON_HOLD_ADMIN_REVIEW branch is RETAINED for FUTURE SEPA/postpay and is NOT
 *   auto-triggered in Phase 1 (only reachable via admin override / future flows).
 *
 * All state-advancing transitions are IDEMPOTENT at the engine level: a transition
 * whose `from` no longer matches the current status is a no-op (webhooks/jobs retry).
 */

/** Named side-effects an engine must (re)confirm when a transition fires. */
export const TransitionEffect = {
  /** Persist the order, assign a human order code. */
  PERSIST_AND_CODE: 'PERSIST_AND_CODE',
  /** HoldedPort.findOrCreateContact for the client. */
  HOLDED_FIND_OR_CREATE_CONTACT: 'HOLDED_FIND_OR_CREATE_CONTACT',
  /** HoldedPort.createProforma — authoritative totals come back from Holded. */
  HOLDED_CREATE_PROFORMA: 'HOLDED_CREATE_PROFORMA',
  /** HoldedPort.sendDocument(proforma) — Holded emails the client. */
  HOLDED_SEND_PROFORMA: 'HOLDED_SEND_PROFORMA',
  /** HoldedPort.createInvoice. */
  HOLDED_CREATE_INVOICE: 'HOLDED_CREATE_INVOICE',
  /** HoldedPort.sendDocument(invoice). */
  HOLDED_SEND_INVOICE: 'HOLDED_SEND_INVOICE',
  /** HoldedPort.createWaybill. */
  HOLDED_CREATE_WAYBILL: 'HOLDED_CREATE_WAYBILL',
  /** SendcloudPort.createLabel. */
  SENDCLOUD_CREATE_LABEL: 'SENDCLOUD_CREATE_LABEL',
  /** EmailPort transactional notify: tracking is ready. */
  NOTIFY_TRACKING_READY: 'NOTIFY_TRACKING_READY',
  /** Schedule the SendcloudPort.getTracking poll job. */
  SCHEDULE_TRACKING_POLL: 'SCHEDULE_TRACKING_POLL',
  /** EmailPort transactional notify: delivered. */
  NOTIFY_DELIVERED: 'NOTIFY_DELIVERED',
  /** EmailPort transactional notify: delivery problem (alert admin/agent). */
  NOTIFY_DELIVERY_PROBLEM: 'NOTIFY_DELIVERY_PROBLEM',
  /** Record the actor + reason for an override into the audit log. */
  RECORD_OVERRIDE_REASON: 'RECORD_OVERRIDE_REASON',
} as const;
export type TransitionEffect =
  (typeof TransitionEffect)[keyof typeof TransitionEffect];

/** What can legitimately trigger a transition. */
export const TransitionTrigger = {
  /** An API/user action (e.g. agent creates an order). */
  USER_ACTION: 'USER_ACTION',
  /** Stripe/PayPal payment webhook. */
  PAYMENT_WEBHOOK: 'PAYMENT_WEBHOOK',
  /** Admin/accountant manual confirmation (IBAN). */
  MANUAL_CONFIRMATION: 'MANUAL_CONFIRMATION',
  /** A background job (e.g. tracking poll). */
  SCHEDULED_JOB: 'SCHEDULED_JOB',
  /** Admin/super-admin manual status override (re-confirms implied automations). */
  ADMIN_OVERRIDE: 'ADMIN_OVERRIDE',
} as const;
export type TransitionTrigger =
  (typeof TransitionTrigger)[keyof typeof TransitionTrigger];

export interface OrderTransition {
  readonly from: OrderStatus;
  readonly to: OrderStatus;
  /** Side-effects the engine must (re)confirm when this transition fires. */
  readonly effects: readonly TransitionEffect[];
  /** Legitimate triggers for this transition. */
  readonly triggers: readonly TransitionTrigger[];
  /** True if reserved for a future phase (SEPA/postpay) and not auto-triggered in Phase 1. */
  readonly future?: boolean;
  /** Human-readable description. */
  readonly description: string;
}

/**
 * The canonical transition table. `create` is modelled as the implicit
 * `NEW → PROFORMA_SENT` automation that runs right after an order is persisted.
 */
export const ORDER_TRANSITIONS: readonly OrderTransition[] = [
  {
    from: OrderStatus.NEW,
    to: OrderStatus.PROFORMA_SENT,
    triggers: [TransitionTrigger.USER_ACTION],
    effects: [
      TransitionEffect.PERSIST_AND_CODE,
      TransitionEffect.HOLDED_FIND_OR_CREATE_CONTACT,
      TransitionEffect.HOLDED_CREATE_PROFORMA,
      TransitionEffect.HOLDED_SEND_PROFORMA,
    ],
    description:
      'Order created: persist + code, ensure Holded contact, create proforma (authoritative totals), Holded emails proforma.',
  },
  {
    from: OrderStatus.PROFORMA_SENT,
    to: OrderStatus.PAID,
    triggers: [
      TransitionTrigger.PAYMENT_WEBHOOK,
      TransitionTrigger.MANUAL_CONFIRMATION,
    ],
    effects: [],
    description:
      'Payment confirmed: Stripe/PayPal webhook (auto) or IBAN admin/accountant manual confirmation.',
  },
  {
    from: OrderStatus.PAID,
    to: OrderStatus.INVOICED,
    triggers: [TransitionTrigger.SCHEDULED_JOB, TransitionTrigger.USER_ACTION],
    effects: [
      TransitionEffect.HOLDED_CREATE_INVOICE,
      TransitionEffect.HOLDED_SEND_INVOICE,
    ],
    description: 'Paid: create Holded invoice + Holded emails it.',
  },
  {
    from: OrderStatus.INVOICED,
    to: OrderStatus.SHIPPED,
    triggers: [TransitionTrigger.SCHEDULED_JOB, TransitionTrigger.USER_ACTION],
    effects: [
      TransitionEffect.HOLDED_CREATE_WAYBILL,
      TransitionEffect.SENDCLOUD_CREATE_LABEL,
      TransitionEffect.NOTIFY_TRACKING_READY,
      TransitionEffect.SCHEDULE_TRACKING_POLL,
    ],
    description:
      'Invoiced: create Holded waybill + Sendcloud label, notify tracking-ready, schedule tracking poll.',
  },
  {
    from: OrderStatus.SHIPPED,
    to: OrderStatus.DELIVERED,
    triggers: [TransitionTrigger.SCHEDULED_JOB],
    effects: [TransitionEffect.NOTIFY_DELIVERED],
    description: 'Tracking poll reports delivered.',
  },
  {
    from: OrderStatus.SHIPPED,
    to: OrderStatus.DELIVERY_PROBLEM,
    triggers: [TransitionTrigger.SCHEDULED_JOB],
    effects: [TransitionEffect.NOTIFY_DELIVERY_PROBLEM],
    description: 'Tracking poll reports a delivery problem.',
  },

  // ── FUTURE BRANCH (SEPA / postpay) — retained, NOT auto-triggered in Phase 1 ──
  {
    from: OrderStatus.PROFORMA_SENT,
    to: OrderStatus.ON_HOLD_ADMIN_REVIEW,
    triggers: [TransitionTrigger.ADMIN_OVERRIDE],
    effects: [TransitionEffect.RECORD_OVERRIDE_REASON],
    future: true,
    description:
      'FUTURE (SEPA/postpay): order held for admin review. Phase 1 only via admin override.',
  },
  {
    from: OrderStatus.ON_HOLD_ADMIN_REVIEW,
    to: OrderStatus.PAID,
    triggers: [TransitionTrigger.MANUAL_CONFIRMATION],
    effects: [],
    future: true,
    description:
      'FUTURE (SEPA/postpay): admin clears the hold and confirms payment terms.',
  },
] as const;

/** Map of `from` status → its outgoing transitions, for O(1) lookup. */
export const ORDER_TRANSITIONS_BY_FROM: Readonly<
  Record<OrderStatus, readonly OrderTransition[]>
> = ORDER_TRANSITIONS.reduce(
  (acc, t) => {
    (acc[t.from] ??= []).push(t);
    return acc;
  },
  {} as Record<OrderStatus, OrderTransition[]>,
);

/** Look up a single transition definition. Returns undefined if not allowed. */
export function findTransition(
  from: OrderStatus,
  to: OrderStatus,
): OrderTransition | undefined {
  return ORDER_TRANSITIONS.find((t) => t.from === from && t.to === to);
}

/** Whether `from → to` is a declared transition (ignores trigger gating). */
export function isTransitionAllowed(
  from: OrderStatus,
  to: OrderStatus,
): boolean {
  return findTransition(from, to) !== undefined;
}

/**
 * Whether `from → to` is a declared transition that is RESERVED for a future phase
 * (`future: true`, e.g. the SEPA/postpay ON_HOLD_ADMIN_REVIEW branch). Such a
 * transition is kept in the table as a stub but must NOT be selectable in Phase 1 —
 * callers (e.g. the manual-override path) use this to reject it. Returns false for
 * undeclared transitions and for the normal Phase-1 edges.
 */
export function isFutureTransition(
  from: OrderStatus,
  to: OrderStatus,
): boolean {
  return findTransition(from, to)?.future === true;
}

/** Terminal statuses have no declared outgoing transition. */
export const TERMINAL_STATUSES: readonly OrderStatus[] = [
  OrderStatus.DELIVERED,
  OrderStatus.DELIVERY_PROBLEM,
];
